/**
 * CharacterSelect — выпадающий список персонажей (оверлей через portal, без роста высоты скролла)
 */

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { ucpService } from "../../../api/services/ucpService";

const CharacterSelect = ({ onSelect }) => {
    const [characters, setCharacters] = useState([]);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [menuRect, setMenuRect] = useState(null);

    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await ucpService.getUserCharacters();
                const chars = result?.characters || [];
                setCharacters(chars);
                if (chars.length > 0) {
                    setSelectedCharacter(chars[0]);
                    onSelect(chars[0]);
                }
            } catch (err) {
                console.error("Ошибка загрузки данных:", err.message);
            }
        };

        fetchData();
    }, [onSelect]);

    const updateMenuRect = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setMenuRect({
            top: r.bottom + 6,
            left: r.left,
            width: r.width,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuRect(null);
            return;
        }
        updateMenuRect();
    }, [isOpen, updateMenuRect]);

    useEffect(() => {
        if (!isOpen) return;

        const onReposition = () => updateMenuRect();
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);

        return () => {
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
    }, [isOpen, updateMenuRect]);

    useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const onPointerDown = (e) => {
            const t = e.target;
            if (triggerRef.current?.contains(t)) return;
            if (menuRef.current?.contains(t)) return;
            setIsOpen(false);
        };

        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [isOpen]);

    const handleSelect = (character) => {
        setSelectedCharacter(character);
        onSelect(character);
        setIsOpen(false);
    };

    const dropdown =
        isOpen &&
        menuRect &&
        typeof document !== "undefined" &&
        createPortal(
            // Обёртка с .character-select нужна: стили вложены как .character-select .character-select__list
            // и без предка список в body не получает position/z-index и смешивается с контентом скролла.
            <div className="character-select character-select--portal-root">
                <ul
                    ref={menuRef}
                    className="character-select__list character-select__list--portal"
                    role="listbox"
                    style={{
                        top: `${menuRect.top}px`,
                        left: `${menuRect.left}px`,
                        width: `${menuRect.width}px`,
                    }}
                >
                    {characters.map((character) => (
                        <li key={character.id ?? character.name} role="none">
                            <button
                                type="button"
                                role="option"
                                aria-selected={
                                    selectedCharacter?.id === character.id
                                }
                                className="character-select__option"
                                onClick={() => handleSelect(character)}
                            >
                                {character.name}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>,
            document.body,
        );

    return (
        <div
            className={`character-select${isOpen ? " character-select--open" : ""}`}
        >
            <button
                ref={triggerRef}
                type="button"
                className="character-select__trigger"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                onClick={() => setIsOpen((o) => !o)}
            >
                <span className="character-select__value">
                    {selectedCharacter
                        ? selectedCharacter.name
                        : "Выберите персонажа"}
                </span>
            </button>
            {dropdown}
        </div>
    );
};

export default CharacterSelect;
